import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Tooltip from '@/components/UI/Tooltip';
import { _isNil } from '@/littledash';
import cn from 'classnames';
import type { FC } from 'react';
import type { DataTableCellIndexCoordinate } from '@/components/UI/DataTableV2/DataTable.model';
import { DateUtils } from '@/utils/Date.utils';
import styles from '../DataTableV2.module.scss';
import { PickerDisplayFormat, usePickerCellBehavior } from '../hooks/usePickerCellBehavior';
import { useAutoInsertOnActivate } from '../hooks/useAutoInsertOnActivate';

interface DataTableTimestampCellProps {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
}

export const DataTableTimestampCell: FC<DataTableTimestampCellProps> = ({ coordinate, positionOffset }) => {
  const { cell, picker, mouseHandlers } = usePickerCellBehavior({
    coordinate,
    positionOffset,
    displayFormat: PickerDisplayFormat.DateTimeHoursMinutesSeconds,
  });

  // Auto-insert current datetime when cell activated on empty cell
  useAutoInsertOnActivate({
    coordinate,
    getValue: () => DateUtils.dateTimeNow(),
  });

  return (
    <div
      className={cn(styles.CellContainer, { [styles.groupBorder]: cell.hasGroupBorder })}
      style={picker.positionStyle}
      onMouseDown={mouseHandlers.onMouseDown}
      onMouseEnter={mouseHandlers.onMouseEnter}
      onMouseUp={mouseHandlers.onMouseUp}
      onDoubleClick={picker.activate}
    >
      {cell.state === 'Loading' ? (
        <div className={cn(styles.Cell, styles.Loading)} />
      ) : (
        <Tooltip render={cell.tooltip}>
          <div
            className={cn(
              styles.Cell,
              styles[cell.selectionState],
              { [styles.Active]: picker.active },
              styles[cell.state]
            )}
            data-state={cell.state}
          >
            <span className={styles.TimestampValue}>
              {_isNil(cell.value) ? (
                <>Add Timestamp</>
              ) : (
                <DateTimeRenderer value={cell.value} defaultResponse={cell.value} />
              )}
            </span>
            <input
              className={styles.TimestampInput}
              style={{ visibility: picker.active ? 'visible' : 'hidden' }}
              ref={picker.inputRef}
              type="datetime-local"
              onChange={picker.handleInputChange}
            />
          </div>
        </Tooltip>
      )}
    </div>
  );
};
